import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { signInWithPopup, signInWithPhoneNumber, RecaptchaVerifier, ConfirmationResult } from 'firebase/auth';
import { getFirebaseAuth, getGoogleProvider, getAppleProvider } from '../utils/firebase';
import { useCustomerAuth, type Customer, type BadgePayload } from '../contexts/CustomerAuthContext';
import { Sheet, SheetHeader, SheetBody } from './ui';
import { track } from '../analytics';
import './FirebaseAuthModal.css';

interface FirebaseAuthModalProps {
  onClose: () => void;
  onSuccess: (customer: Customer, token: string, newBadges?: BadgePayload[]) => void;
  orderId?: string;
}

// Debug logger — writes to window.__authLog so the debug panel in OrderStatus can display it
function authLog(msg: string) {
  const line = `[modal] ${msg}`;
  console.log(line);
  if (typeof window !== 'undefined') {
    const w = window as unknown as Record<string, string[]>;
    w.__authLog = w.__authLog || [];
    w.__authLog.push(`${new Date().toISOString().slice(11, 19)} ${line}`);
  }
}

function FirebaseAuthModal({ onClose, onSuccess, orderId }: FirebaseAuthModalProps) {
  const { t } = useTranslation();
  const { loginWithFirebaseToken, devLogin } = useCustomerAuth();
  const isDev = import.meta.env.DEV || import.meta.env.VITE_ALLOW_DEV_LOGIN === 'true';
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneStep, setPhoneStep] = useState<'enter' | 'verify'>('enter');
  const confirmationRef = useRef<ConfirmationResult | null>(null);
  const authRef = useRef<Awaited<ReturnType<typeof getFirebaseAuth>>>(null);
  const [authReady, setAuthReady] = useState(false);
  const [code, setCode] = useState('');
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const successDataRef = useRef<{ customer: Customer; token: string; newBadges?: BadgePayload[] } | null>(null);
  const providerInProgressRef = useRef(false);

  useEffect(() => {
    authLog(`init: standalone=${(window.navigator as { standalone?: boolean }).standalone}, displayMode=${window.matchMedia('(display-mode: standalone)').matches}, iOS=${/iPad|iPhone|iPod/.test(navigator.userAgent)}`);
    getFirebaseAuth().then((a) => {
      authRef.current = a;
      setAuthReady(!!a);
      authLog(`firebase: ready=${!!a}, authDomain=${a ? (a as unknown as { config?: { authDomain?: string } }).config?.authDomain : 'null'}`);
    });
  }, []);

  const triggerSuccess = (customer: Customer, token: string, newBadges?: BadgePayload[]) => {
    track('auth_completed', {
      customerId: customer.id,
      orderId,
      hasOrderContext: !!orderId,
    });
    successDataRef.current = { customer, token, newBadges };
    setShowSuccess(true);
    setLoading(null);
    setTimeout(() => {
      const d = successDataRef.current;
      if (d) onSuccess(d.customer, d.token, d.newBadges);
    }, 450);
  };

  const handleProvider = async (providerName: 'google' | 'apple') => {
    if (providerInProgressRef.current) return;
    const auth = authRef.current;
    if (!auth) {
      setError(t('foodPassport.firebaseNotConfigured'));
      return;
    }
    providerInProgressRef.current = true;
    setError('');
    setLoading(providerName);
    track('auth_started', { method: providerName, orderId });
    authLog(`provider: ${providerName} starting signInWithPopup`);

    const tryPopup = async (attempt: number) => {
      const provider = providerName === 'google' ? getGoogleProvider() : getAppleProvider();
      authLog(`provider: attempt ${attempt}`);
      const result = await signInWithPopup(auth, provider);
      return result;
    };

    try {
      let result;
      try {
        result = await tryPopup(1);
      } catch (firstErr: unknown) {
        const firstCode = (firstErr as { code?: string })?.code ?? '';
        authLog(`provider: first attempt failed: ${firstCode}`);
        // Auto-retry once for Apple (known first-attempt failure on iOS Safari)
        if (providerName === 'apple' && firstCode !== 'auth/popup-closed-by-user') {
          authLog('provider: auto-retrying apple');
          result = await tryPopup(2);
        } else {
          throw firstErr;
        }
      }
      authLog(`provider: popup returned, uid=${result.user.uid.slice(0, 8)}`);
      setLoading('finishing');
      const idToken = await result.user.getIdToken();
      authLog(`provider: got idToken, calling loginWithFirebaseToken`);
      const { customer, token, newBadges } = await loginWithFirebaseToken(idToken, orderId);
      authLog(`provider: login OK, customer=${customer.id.slice(0, 8)}`);
      triggerSuccess(customer, token, newBadges);
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      const code = e?.code ?? '';
      const msg = e?.message ?? '';
      authLog(`provider: ERROR code=${code} msg=${msg.slice(0, 100)}`);
      const isPopupClosed = code === 'auth/popup-closed-by-user';
      const isPopupBlocked =
        code === 'auth/popup-blocked' ||
        code === 'auth/cancelled-popup-request' ||
        (msg.toLowerCase().includes('popup') && (msg.toLowerCase().includes('block') || msg.toLowerCase().includes('cancel')));
      if (isPopupClosed) {
        authLog('provider: popup closed by user');
        setError('');
      } else if (isPopupBlocked) {
        authLog('provider: popup blocked');
        setError(t('foodPassport.authFailed'));
      } else {
        setError(msg || t('foodPassport.authFailed'));
      }
    } finally {
      providerInProgressRef.current = false;
      if (!successDataRef.current) setLoading(null);
    }
  };

  const handlePhoneSend = async () => {
    if (!phone.trim()) return;
    setError('');
    setLoading('phone');
    track('auth_started', { method: 'phone', orderId });
    try {
      const auth = await getFirebaseAuth();
      if (!auth) {
        setError(t('foodPassport.firebaseNotConfigured'));
        return;
      }
      // Always clear and recreate reCAPTCHA verifier to avoid stale DOM references
      const win = window as unknown as { recaptchaVerifier?: RecaptchaVerifier };
      if (win.recaptchaVerifier) {
        try { win.recaptchaVerifier.clear(); } catch { /* already cleared */ }
        win.recaptchaVerifier = undefined;
      }
      win.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {},
      });
      const cleanPhone = phone.replace(/\s+/g, '').trim();
      const confirmation = await signInWithPhoneNumber(auth, cleanPhone, win.recaptchaVerifier);
      confirmationRef.current = confirmation;
      setPhoneStep('verify');
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || t('foodPassport.authFailed'));
    } finally {
      setLoading(null);
    }
  };

  const handlePhoneVerify = async () => {
    if (!confirmationRef.current || !code.trim()) return;
    setError('');
    setLoading('phone');
    try {
      const result = await confirmationRef.current.confirm(code.trim());
      const idToken = await result.user.getIdToken();
      const { customer, token, newBadges } = await loginWithFirebaseToken(idToken, orderId);
      triggerSuccess(customer, token, newBadges);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || t('foodPassport.authFailed'));
    } finally {
      setLoading(null);
    }
  };

  const phoneSection = phoneStep === 'enter' ? (
    <div className="firebase-auth-phone-step">
      <div className="firebase-auth-phone-step-header">
        <button
          type="button"
          className="firebase-auth-phone-back"
          onClick={() => { setPhoneStep('enter'); setPhone(''); setError(''); setShowPhoneInput(false); }}
          aria-label="Back"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <span className="firebase-auth-phone-step-title">{t('foodPassport.continueWithPhone')}</span>
      </div>
      <input
        type="tel"
        placeholder={t('foodPassport.phonePlaceholder')}
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        className="firebase-auth-phone-input"
        autoFocus
      />
      <button
        type="button"
        className="firebase-auth-btn firebase-auth-phone"
        onClick={handlePhoneSend}
        disabled={!!loading || !phone.trim()}
      >
        {loading === 'phone' ? (
          <span className="firebase-auth-btn-spinner" />
        ) : t('foodPassport.sendCode')}
      </button>
    </div>
  ) : (
    <div className="firebase-auth-phone-step">
      <div className="firebase-auth-phone-step-header">
        <button
          type="button"
          className="firebase-auth-phone-back"
          onClick={() => { setPhoneStep('enter'); setCode(''); setError(''); }}
          aria-label="Back"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <span className="firebase-auth-phone-step-title">{t('foodPassport.enterCode')}</span>
      </div>
      <p className="firebase-auth-phone-hint">{t('foodPassport.codeSentTo')} <strong>{phone}</strong></p>
      <div className="firebase-auth-code-inputs">
        {Array.from({ length: 6 }).map((_, i) => (
          <input
            key={i}
            type="text"
            inputMode="numeric"
            maxLength={1}
            className="firebase-auth-code-cell"
            value={code[i] || ''}
            autoFocus={i === 0}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              if (!val && !code[i]) return;
              const newCode = code.split('');
              newCode[i] = val;
              const joined = newCode.join('').slice(0, 6);
              setCode(joined);
              // Auto-advance to next cell
              if (val && i < 5) {
                const next = e.target.parentElement?.children[i + 1] as HTMLInputElement;
                next?.focus();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !code[i] && i > 0) {
                const prev = (e.target as HTMLElement).parentElement?.children[i - 1] as HTMLInputElement;
                prev?.focus();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
              setCode(pasted);
              const target = (e.target as HTMLElement).parentElement?.children[Math.min(pasted.length, 5)] as HTMLInputElement;
              target?.focus();
            }}
          />
        ))}
      </div>
      <button
        type="button"
        className="firebase-auth-btn firebase-auth-phone"
        onClick={handlePhoneVerify}
        disabled={!!loading || code.length < 4}
      >
        {loading === 'phone' ? (
          <span className="firebase-auth-btn-spinner" />
        ) : t('foodPassport.verify')}
      </button>
    </div>
  );

  const closeHandler = showSuccess ? () => {} : onClose;

  return (
    <Sheet open={true} onClose={closeHandler} className="firebase-auth-sheet">
      <SheetHeader className="firebase-auth-header">
        <img src="/logo-olso.png" alt="OLSO" className="firebase-auth-logo" />
        <h2 className="firebase-auth-title">{t('foodPassport.createPassport')}</h2>
        <p className="firebase-auth-subtitle">{t('foodPassport.ctaDesc')}</p>
      </SheetHeader>
      <SheetBody className="firebase-auth-body">
        {error && <div className="firebase-auth-error">{error}</div>}
        <div id="recaptcha-container" />

        {showSuccess ? (
          <div className="firebase-auth-success">
            <div className="firebase-auth-success-circle">
              <svg className="firebase-auth-success-check" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="firebase-auth-success-text">{t('foodPassport.welcomeBack', 'Добро пожаловать!')}</div>
          </div>
        ) : loading === 'finishing' ? (
          <div className="firebase-auth-finishing">
            <div className="firebase-auth-finishing-spinner" />
            <div className="firebase-auth-finishing-text">{t('foodPassport.signingIn')}...</div>
          </div>
        ) : showPhoneInput ? (
          <div className="firebase-auth-buttons firebase-auth-slide-in">
            {phoneSection}
          </div>
        ) : (
          <div className="firebase-auth-buttons">
            <button
              type="button"
              className="firebase-auth-btn firebase-auth-apple"
              onClick={() => handleProvider('apple')}
              disabled={!!loading || !authReady}
            >
              <svg className="firebase-auth-apple-icon" viewBox="0 0 814 1000" aria-hidden="true">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57.8-155.5-127.4c-58.8-82-106.5-209.6-106.5-330.8 0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.3zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 103.7-30.4 135.5-71.3z"/>
              </svg>
              {!authReady ? t('foodPassport.signingIn') : loading === 'apple' ? t('foodPassport.signingIn') : t('foodPassport.continueWithApple')}
            </button>
            <button
              type="button"
              className="firebase-auth-btn firebase-auth-google"
              onClick={() => handleProvider('google')}
              disabled={!!loading || !authReady}
            >
              <svg className="firebase-auth-google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {!authReady ? t('foodPassport.signingIn') : loading === 'google' ? t('foodPassport.signingIn') : t('foodPassport.continueWithGoogle')}
            </button>
            <div className="firebase-auth-divider">
              <span>{t('common.or')}</span>
            </div>
            <button
              type="button"
              className="firebase-auth-btn firebase-auth-phone-trigger"
              onClick={() => setShowPhoneInput(true)}
              disabled={!!loading || !authReady}
            >
              <svg className="firebase-auth-phone-icon" viewBox="0 0 24 24" fill="none">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('foodPassport.continueWithPhone')}
            </button>
            {isDev && (
              <button
                type="button"
                className="firebase-auth-btn"
                style={{ marginTop: 12, background: '#fff3e0', color: '#8B4500', border: '1px dashed #c97a26', fontWeight: 700 }}
                onClick={async () => {
                  const email = window.prompt('DEV: email для входа', 'tester@olso.local');
                  if (!email) return;
                  setLoading('dev');
                  setError('');
                  try {
                    const { customer, token } = await devLogin(email);
                    triggerSuccess(customer, token);
                  } catch (e) {
                    setError((e as Error).message || 'Dev login failed');
                    setLoading(null);
                  }
                }}
                disabled={!!loading}
              >
                🛠 DEV login (bypass Firebase)
              </button>
            )}
          </div>
        )}
      </SheetBody>
    </Sheet>
  );
}

export default FirebaseAuthModal;
